import type { ParsedSlide } from "./carousel-parser";
import type { CarouselTemplate } from "./carousel-templates";
import { SLIDE_WIDTH, SLIDE_HEIGHT } from "./carousel-templates";
import { VpStyleSlide, hasVpStyle } from "./carousel-styles/vp-renderers";

interface CarouselSlideProps {
  slide: ParsedSlide;
  template: CarouselTemplate;
  slideIndex: number;
  totalSlides: number;
  profileHandle?: string;
  /** Brand-asset URL rendered as the avatar in the top-left of every slide. */
  profileAvatarUrl?: string;
  /** Display name shown next to the avatar (e.g. "Paweł Kowalski"). */
  profileName?: string;
}

// ── Layout renderers ──────────────────────────────────────

function ClassicLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const titleSize = slide.isCover ? template.titleFontSize * 1.15 : template.titleFontSize;
  const bodySize = slide.isCover ? template.bodyFontSize * 1.1 : template.bodyFontSize;
  const titleWeight = template.titleWeight || 800;

  return (
    <>
      {!slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 48,
            right: 64,
            color: template.accentColor,
            fontSize: 28,
            fontWeight: 700,
            opacity: 0.7,
          }}
        >
          {slideIndex + 1}/{totalSlides}
        </div>
      )}
      {slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            backgroundColor: template.accentColor,
          }}
        />
      )}
      <h2
        style={{
          color: template.titleColor,
          fontSize: titleSize,
          fontWeight: titleWeight,
          lineHeight: 1.2,
          marginBottom: 32,
          letterSpacing: "-0.02em",
          textTransform: template.titleTransform || "none",
        }}
      >
        {slide.title}
      </h2>
      {slide.body && (
        <div
          style={{
            color: template.bodyColor,
            fontSize: bodySize,
            fontWeight: template.bodyWeight || 400,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
          }}
        >
          {slide.body}
        </div>
      )}
      {slide.isCTA && (
        <div
          style={{
            marginTop: 48,
            width: 80,
            height: 6,
            backgroundColor: template.accentColor,
            borderRadius: 3,
          }}
        />
      )}
      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

function CardLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const accent = template.accentColor;
  const accent2 = template.accentColor2 || accent;
  const decorColor = template.decorColor || "rgba(255,255,255,0.06)";

  return (
    <>
      {/* Decorative circles */}
      <div
        style={{
          position: "absolute",
          top: -60,
          right: -60,
          width: 240,
          height: 240,
          borderRadius: "50%",
          background: decorColor,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -40,
          left: -40,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: decorColor,
        }}
      />

      {/* Top accent line */}
      <div
        style={{
          position: "absolute",
          top: 48,
          left: 72,
          right: 72,
          height: 3,
          background: `linear-gradient(90deg, ${accent}, ${accent2})`,
          borderRadius: 2,
          opacity: 0.6,
        }}
      />

      {/* Slide number badge */}
      {!slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 36,
            right: 64,
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${accent}, ${accent2})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            fontSize: 22,
            fontWeight: 800,
          }}
        >
          {slideIndex + 1}
        </div>
      )}

      {/* Card container */}
      <div
        style={{
          margin: "80px 56px 56px",
          padding: "56px 48px",
          borderRadius: 24,
          background: slide.isCover
            ? template.coverBackground || "rgba(255,255,255,0.05)"
            : "rgba(255,255,255,0.04)",
          border: `1px solid ${decorColor}`,
          backdropFilter: "blur(10px)",
          flex: 1,
          display: "flex",
          flexDirection: "column" as const,
          justifyContent: "center" as const,
        }}
      >
        {slide.isCover && (
          <div
            style={{
              width: 64,
              height: 6,
              background: `linear-gradient(90deg, ${accent}, ${accent2})`,
              borderRadius: 3,
              marginBottom: 36,
            }}
          />
        )}
        <h2
          style={{
            color: template.titleColor,
            fontSize: slide.isCover ? template.titleFontSize * 1.2 : template.titleFontSize,
            fontWeight: template.titleWeight || 800,
            lineHeight: 1.15,
            marginBottom: 28,
            letterSpacing: "-0.03em",
            textTransform: template.titleTransform || "none",
          }}
        >
          {slide.title}
        </h2>
        {slide.body && (
          <div
            style={{
              color: template.bodyColor,
              fontSize: template.bodyFontSize,
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {slide.body}
          </div>
        )}
        {slide.isCTA && (
          <div
            style={{
              marginTop: 40,
              padding: "20px 40px",
              borderRadius: 16,
              background: `linear-gradient(135deg, ${accent}, ${accent2})`,
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              textAlign: "center" as const,
              letterSpacing: "0.02em",
            }}
          >
            {slide.body ? "→" : "Sprawdź →"}
          </div>
        )}
      </div>

      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

function BoldNumberLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const accent = template.accentColor;
  const accent2 = template.accentColor2 || accent;

  return (
    <>
      {slide.isCover ? (
        <>
          {/* Cover: big accent block + title */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "35%",
              background: `linear-gradient(180deg, ${accent}, ${accent2})`,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "35%",
              display: "flex",
              alignItems: "flex-end",
              padding: "0 72px 40px",
            }}
          >
            <div
              style={{
                width: 80,
                height: 6,
                backgroundColor: "#FFFFFF",
                borderRadius: 3,
                opacity: 0.5,
              }}
            />
          </div>
          <div style={{ marginTop: "auto", padding: "0" }}>
            <h2
              style={{
                color: template.titleColor,
                fontSize: template.titleFontSize * 1.35,
                fontWeight: 900,
                lineHeight: 1.05,
                letterSpacing: "-0.04em",
                textTransform: template.titleTransform || "uppercase",
              }}
            >
              {slide.title}
            </h2>
            {slide.body && (
              <div
                style={{
                  color: template.bodyColor,
                  fontSize: template.bodyFontSize * 1.1,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  marginTop: 28,
                  whiteSpace: "pre-wrap",
                }}
              >
                {slide.body}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Content slides: giant number + divider + text */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 0, flex: 1 }}>
            {/* Big number */}
            <div
              style={{
                fontSize: 220,
                fontWeight: 900,
                lineHeight: 0.85,
                color: accent,
                opacity: 0.15,
                position: "absolute",
                top: 40,
                left: 56,
                fontFamily: template.fontFamily,
              }}
            >
              {String(slideIndex).padStart(2, "0")}
            </div>
            <div
              style={{
                fontSize: 72,
                fontWeight: 900,
                color: accent,
                marginRight: 32,
                lineHeight: 1,
                minWidth: 80,
                fontFamily: template.fontFamily,
              }}
            >
              {String(slideIndex).padStart(2, "0")}
            </div>
            {/* Vertical divider */}
            <div
              style={{
                width: 4,
                alignSelf: "stretch",
                background: `linear-gradient(180deg, ${accent}, transparent)`,
                borderRadius: 2,
                marginRight: 32,
                opacity: 0.6,
                maxHeight: 300,
              }}
            />
            {/* Content */}
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  color: template.titleColor,
                  fontSize: template.titleFontSize,
                  fontWeight: 900,
                  lineHeight: 1.15,
                  marginBottom: 24,
                  letterSpacing: "-0.02em",
                  textTransform: template.titleTransform || "none",
                }}
              >
                {slide.title}
              </h2>
              {slide.body && (
                <div
                  style={{
                    color: template.bodyColor,
                    fontSize: template.bodyFontSize,
                    fontWeight: 400,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {slide.body}
                </div>
              )}
            </div>
          </div>
          {slide.isCTA && (
            <div
              style={{
                marginTop: 32,
                padding: "18px 0",
                borderTop: `3px solid ${accent}`,
                color: accent,
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: "0.05em",
                textTransform: "uppercase" as const,
              }}
            >
              → {slide.title}
            </div>
          )}
        </>
      )}
      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

function SplitLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const accent = template.decorColor || template.accentColor;
  const accent2 = template.accentColor2 || accent;

  if (slide.isCover) {
    return (
      <>
        {/* Cover: full accent background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              template.coverBackground || `linear-gradient(180deg, ${accent}, ${accent2})`,
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            padding: "80px 72px",
          }}
        >
          <div
            style={{
              width: 80,
              height: 6,
              backgroundColor: "rgba(255,255,255,0.5)",
              borderRadius: 3,
              marginBottom: 40,
            }}
          />
          <h2
            style={{
              color: "#FFFFFF",
              fontSize: template.titleFontSize * 1.3,
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
            }}
          >
            {slide.title}
          </h2>
          {slide.body && (
            <div
              style={{
                color: "rgba(255,255,255,0.8)",
                fontSize: template.bodyFontSize * 1.05,
                fontWeight: 400,
                lineHeight: 1.5,
                marginTop: 28,
                whiteSpace: "pre-wrap",
              }}
            >
              {slide.body}
            </div>
          )}
        </div>
        <SlideFooter
          template={{ ...template, bodyColor: "rgba(255,255,255,0.5)", accentColor: "#FFFFFF" }}
          slideIndex={slideIndex}
          totalSlides={totalSlides}
          profileHandle={profileHandle}
        />
      </>
    );
  }

  return (
    <>
      {/* Left accent strip */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 12,
          height: "100%",
          background: `linear-gradient(180deg, ${accent}, ${accent2})`,
        }}
      />
      {/* Top decorative line */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 12,
          right: 0,
          height: 1,
          backgroundColor: `${accent}20`,
        }}
      />

      {/* Slide counter */}
      <div
        style={{
          position: "absolute",
          top: 44,
          right: 64,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ width: 32, height: 3, backgroundColor: accent, borderRadius: 2 }} />
        <span style={{ color: accent, fontSize: 24, fontWeight: 700 }}>{slideIndex + 1}</span>
      </div>

      {/* Content with left padding for strip */}
      <div style={{ paddingLeft: 32 }}>
        <h2
          style={{
            color: template.titleColor,
            fontSize: template.titleFontSize,
            fontWeight: 800,
            lineHeight: 1.15,
            marginBottom: 28,
            letterSpacing: "-0.02em",
          }}
        >
          {slide.title}
        </h2>
        {slide.body && (
          <div
            style={{
              color: template.bodyColor,
              fontSize: template.bodyFontSize,
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {slide.body}
          </div>
        )}
      </div>

      {slide.isCTA && (
        <div style={{ paddingLeft: 32, marginTop: 40 }}>
          <div
            style={{
              display: "inline-block",
              padding: "16px 48px",
              borderRadius: 12,
              backgroundColor: accent,
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            Sprawdź →
          </div>
        </div>
      )}

      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

function EditorialLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const accent = template.accentColor;

  return (
    <>
      {/* Top rule line */}
      <div
        style={{
          position: "absolute",
          top: 48,
          left: 72,
          right: 72,
          height: 2,
          backgroundColor: accent,
          opacity: slide.isCover ? 1 : 0.2,
        }}
      />
      {/* Bottom rule line */}
      <div
        style={{
          position: "absolute",
          bottom: 100,
          left: 72,
          right: 72,
          height: 1,
          backgroundColor: accent,
          opacity: 0.1,
        }}
      />

      {/* Slide label (top left, editorial style) */}
      {!slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 64,
            left: 72,
            color: template.accentColor2 || accent,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "0.15em",
            textTransform: "uppercase" as const,
          }}
        >
          {slide.label || `Punkt ${slideIndex}`}
        </div>
      )}

      {/* Page number (top right) */}
      {!slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 64,
            right: 72,
            color: accent,
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "0.1em",
          }}
        >
          {String(slideIndex + 1).padStart(2, "0")} / {String(totalSlides).padStart(2, "0")}
        </div>
      )}

      {/* Content area pushed down for editorial breathing room */}
      <div style={{ marginTop: slide.isCover ? 0 : 60 }}>
        <h2
          style={{
            color: template.titleColor,
            fontSize: slide.isCover ? template.titleFontSize * 1.4 : template.titleFontSize,
            fontWeight: template.titleWeight || 900,
            lineHeight: slide.isCover ? 1.0 : 1.15,
            marginBottom: slide.isCover ? 40 : 32,
            letterSpacing: "-0.04em",
            textTransform: template.titleTransform || "uppercase",
          }}
        >
          {slide.title}
        </h2>

        {slide.isCover && (
          <div style={{ width: 120, height: 4, backgroundColor: accent, marginBottom: 32 }} />
        )}

        {slide.body && (
          <div
            style={{
              color: template.bodyColor,
              fontSize: template.bodyFontSize,
              fontWeight: 400,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              maxWidth: 860,
            }}
          >
            {slide.body}
          </div>
        )}
      </div>

      {slide.isCTA && (
        <div
          style={{
            marginTop: 48,
            borderTop: `2px solid ${accent}`,
            paddingTop: 24,
            color: accent,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase" as const,
          }}
        >
          ↗ Działaj teraz
        </div>
      )}

      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

function SpeechBubbleLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  const accent = template.accentColor;
  const accent2 = template.accentColor2 || accent;
  const decorColor = template.decorColor || "rgba(99,102,241,0.1)";
  const isDark = template.background.includes("#1") || template.background.includes("#0");
  const bubbleBg = isDark ? "rgba(255,255,255,0.06)" : "#FFFFFF";
  const bubbleBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  return (
    <>
      {/* Decorative dots pattern */}
      <div style={{ position: "absolute", top: 40, right: 40, opacity: 0.15 }}>
        {[0, 1, 2].map((row) => (
          <div key={row} style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            {[0, 1, 2, 3].map((col) => (
              <div
                key={col}
                style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: accent }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Slide counter (left side, vertical) */}
      {!slide.isCover && (
        <div
          style={{
            position: "absolute",
            top: 48,
            left: 72,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: `linear-gradient(135deg, ${accent}, ${accent2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              fontSize: 20,
              fontWeight: 800,
            }}
          >
            {slideIndex + 1}
          </div>
          <div
            style={{ width: 32, height: 2, backgroundColor: accent, opacity: 0.3, borderRadius: 1 }}
          />
        </div>
      )}

      {/* Main bubble */}
      <div
        style={{
          margin: slide.isCover ? "0" : "80px 0 0 0",
          padding: "52px 56px",
          borderRadius: 32,
          backgroundColor: bubbleBg,
          border: `1px solid ${bubbleBorder}`,
          boxShadow: isDark ? "none" : "0 4px 24px rgba(0,0,0,0.04)",
          position: "relative",
        }}
      >
        {slide.isCover && (
          <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
            {[accent, accent2, decorColor].map((c, i) => (
              <div
                key={i}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: c,
                  opacity: i === 2 ? 0.4 : 1,
                }}
              />
            ))}
          </div>
        )}

        <h2
          style={{
            color: template.titleColor,
            fontSize: slide.isCover ? template.titleFontSize * 1.15 : template.titleFontSize,
            fontWeight: 800,
            lineHeight: 1.2,
            marginBottom: slide.body ? 24 : 0,
            letterSpacing: "-0.02em",
          }}
        >
          {slide.title}
        </h2>

        {slide.body && (
          <div
            style={{
              color: template.bodyColor,
              fontSize: template.bodyFontSize,
              fontWeight: 400,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {slide.body}
          </div>
        )}

        {slide.isCTA && (
          <div
            style={{
              marginTop: 32,
              padding: "16px 40px",
              borderRadius: 20,
              background: `linear-gradient(135deg, ${accent}, ${accent2})`,
              color: "#FFFFFF",
              fontSize: 26,
              fontWeight: 700,
              textAlign: "center" as const,
              display: "inline-block",
            }}
          >
            Sprawdź →
          </div>
        )}

        {/* Bubble tail */}
        <div
          style={{
            position: "absolute",
            bottom: -18,
            left: 72,
            width: 0,
            height: 0,
            borderLeft: "18px solid transparent",
            borderRight: "18px solid transparent",
            borderTop: `20px solid ${bubbleBg}`,
          }}
        />
      </div>

      <SlideFooter
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    </>
  );
}

// ── Shared profile header (top-left avatar + name) ─────────────────────────
//
// Renders nothing if neither avatar nor name is set. Sits in the upper-left
// corner above all layout chrome (z-index 5) so it stays consistent across
// every template — Insta-style identity strip.

function SlideProfileHeader({
  template,
  profileAvatarUrl,
  profileName,
}: {
  template: CarouselTemplate;
  profileAvatarUrl?: string;
  profileName?: string;
}) {
  if (!profileAvatarUrl && !profileName) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 40,
        left: 56,
        display: "flex",
        alignItems: "center",
        gap: 16,
        zIndex: 5,
      }}
    >
      {profileAvatarUrl && (
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            overflow: "hidden",
            border: `2px solid ${template.accentColor}`,
            flexShrink: 0,
            backgroundColor: "rgba(255,255,255,0.05)",
          }}
        >
          <img
            src={profileAvatarUrl}
            alt=""
            crossOrigin="anonymous"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
      )}
      {profileName && (
        <span
          style={{
            color: template.titleColor,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          {profileName}
        </span>
      )}
    </div>
  );
}

// ── Shared footer ──────────────────────────────────────

function SlideFooter({
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: {
  template: CarouselTemplate;
  slideIndex: number;
  totalSlides: number;
  profileHandle?: string;
}) {
  return (
    <>
      {profileHandle && (
        <div
          style={{
            position: "absolute",
            bottom: 48,
            left: 72,
            color: template.bodyColor,
            fontSize: 24,
            fontWeight: 500,
            opacity: 0.5,
          }}
        >
          @{profileHandle}
        </div>
      )}
      {slideIndex < totalSlides - 1 && slideIndex > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 48,
            right: 64,
            color: template.accentColor,
            fontSize: 22,
            fontWeight: 600,
            opacity: 0.6,
          }}
        >
          Przesuń →
        </div>
      )}
    </>
  );
}

// ── VP Style layout (uses JSX renderers) ──────

function VpStyleLayout({
  slide,
  template,
  slideIndex,
  totalSlides,
  profileHandle,
}: CarouselSlideProps) {
  if (!template.vpStyleId || !hasVpStyle(template.vpStyleId)) {
    return (
      <ClassicLayout
        slide={slide}
        template={template}
        slideIndex={slideIndex}
        totalSlides={totalSlides}
        profileHandle={profileHandle}
      />
    );
  }
  return (
    <VpStyleSlide
      styleId={template.vpStyleId}
      slide={slide}
      slideIndex={slideIndex}
      totalSlides={totalSlides}
      profileHandle={profileHandle}
    />
  );
}

// ── Layout dispatcher map ──────────────────────────────

const LAYOUT_RENDERERS: Record<string, React.FC<CarouselSlideProps>> = {
  classic: ClassicLayout,
  card: CardLayout,
  "bold-number": BoldNumberLayout,
  split: SplitLayout,
  editorial: EditorialLayout,
  "speech-bubble": SpeechBubbleLayout,
  "vp-style": VpStyleLayout,
};

// ── Main component ─────────────────────────────────────

export default function CarouselSlide(props: CarouselSlideProps) {
  const { template, profileAvatarUrl, profileName } = props;
  const isGradient = template.background.startsWith("linear-gradient");
  const bgStyle = isGradient
    ? { background: template.background }
    : { backgroundColor: template.background };

  const LayoutRenderer = LAYOUT_RENDERERS[template.layout] || ClassicLayout;
  const hasProfileHeader = !!(profileAvatarUrl || profileName);

  return (
    <div
      className="carousel-slide-render"
      style={{
        width: SLIDE_WIDTH,
        height: SLIDE_HEIGHT,
        ...bgStyle,
        fontFamily: template.fontFamily,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        // Reserve ~140px at the top when a profile header is rendered so the
        // layout's own title/copy doesn't collide with the avatar strip.
        padding:
          template.layout === "card" ? "0" : hasProfileHeader ? "160px 72px 80px" : "80px 72px",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SlideProfileHeader
        template={template}
        profileAvatarUrl={profileAvatarUrl}
        profileName={profileName}
      />
      <LayoutRenderer {...props} />
    </div>
  );
}
